import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Bot, Loader2, Mic } from 'lucide-react'
import { useIncidentDetail } from '../hooks/useIncidentDetail'
import { useUpdateIncidentStatus } from '../hooks/useUpdateIncidentStatus'
import { StatusBadge } from './StatusBadge'
import { PriorityBadge } from './PriorityBadge'
import { nextStatuses } from '../utils/statusTransitions'
import { STATUS_BUTTON_CLASS, STATUS_LABELS } from '../constants/incidentMaps'
import { peopleService } from '../../../services/peopleService'
import { propertiesService } from '../../../services/propertiesService'
import { teamsService } from '../../../services/teamsService'
import { formatRelative } from '../../../utils/formatters'
import type { Incident, IncidentStatus } from '../types/incident.types'
import type { User, Property, Team } from '../../../types/api.types'

interface IncidentDetailModalProps {
  incidentId: number
  onClose: () => void
  prefetched?: Incident
}

export function IncidentDetailModal({ incidentId, onClose, prefetched }: IncidentDetailModalProps): JSX.Element {
  const detailQuery = useIncidentDetail(prefetched ? null : incidentId)
  const updateStatus = useUpdateIncidentStatus()
  const incident = prefetched ?? detailQuery.data
  const isLoading = !prefetched && detailQuery.isLoading
  const isError = !prefetched && detailQuery.isError

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="incident-detail-title"
      className="fixed inset-0 z-50 flex items-center justify-center"
    >
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />
      <div className="relative z-10 mx-3 max-h-[90dvh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-4 shadow-2xl sm:mx-4 sm:p-6">
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" aria-hidden="true" />
          </div>
        )}
        {isError && (
          <div className="py-12 text-center">
            <p className="text-sm font-medium text-red-700">Couldn't load incident.</p>
            <button
              type="button"
              onClick={() => detailQuery.refetch()}
              className="mt-3 rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:opacity-80"
            >
              Try again
            </button>
          </div>
        )}
        {incident && (
          <IncidentDetailBody
            incident={incident}
            onTransition={(status) =>
              updateStatus.mutate({ id: incident.id, body: { status } })
            }
            isUpdating={updateStatus.isPending}
            onClose={onClose}
            readOnly={prefetched != null}
          />
        )}
      </div>
    </div>
  )
}

interface IncidentDetailBodyProps {
  incident: Incident
  onTransition: (status: IncidentStatus) => void
  isUpdating: boolean
  onClose: () => void
  readOnly?: boolean
}

function IncidentDetailBody({ incident, onTransition, isUpdating, onClose, readOnly = false }: IncidentDetailBodyProps): JSX.Element {
  const usersQuery = useQuery<User[]>({
    queryKey: ['users'],
    queryFn: () => peopleService.getAll(),
    staleTime: 5 * 60 * 1000,
    meta: { silent: true },
    enabled: incident.assignee_user_id != null || incident.created_by_user_id != null,
  })

  const propertiesQuery = useQuery<Property[]>({
    queryKey: ['properties'],
    queryFn: () => propertiesService.getAll(),
    staleTime: 5 * 60 * 1000,
    meta: { silent: true },
  })

  const teamsQuery = useQuery<Team[]>({
    queryKey: ['teams'],
    queryFn: () => teamsService.getAll(),
    staleTime: 5 * 60 * 1000,
    meta: { silent: true },
    enabled: incident.assignee_team_id != null,
  })

  const assigneeUser = usersQuery.data?.find((u) => u.id === incident.assignee_user_id)
  const createdByUser = usersQuery.data?.find((u) => u.id === incident.created_by_user_id)
  const assigneeTeam = teamsQuery.data?.find((t) => t.id === incident.assignee_team_id)
  const property = propertiesQuery.data?.find((p) => p.id === incident.property_id)

  const transitions = nextStatuses(incident.status)
  const description = incident.description?.trim() || 'No description.'
  const isDraft = incident.status === 'DRAFT'
  const isAi = incident.source === 'ai'
  const confidencePct = incident.confidence != null ? Math.round(incident.confidence * 100) : null

  return (
    <>
      {/* DRAFT review banner */}
      {isDraft && !readOnly && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3">
          <p className="text-sm font-medium text-amber-800">
            AI suggestion — needs manager review
          </p>
          {confidencePct != null && (
            <p className="mt-0.5 text-xs text-amber-700">
              Confidence: {confidencePct}%
              {incident.intent ? ` · Intent: ${incident.intent}` : ''}
            </p>
          )}
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              disabled={isUpdating}
              onClick={() => onTransition('NEW')}
              className="rounded-lg bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
            >
              Promote to active
            </button>
            <button
              type="button"
              disabled={isUpdating}
              onClick={() => onTransition('CLOSED')}
              className="rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-sm font-medium text-amber-700 hover:bg-amber-50 disabled:opacity-50"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      <header className="mb-4 flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
            Incident #{incident.id}
          </p>
          <h2 id="incident-detail-title" className="mt-1 text-xl font-semibold text-gray-900">
            {incident.title}
          </h2>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <StatusBadge status={incident.status} />
            <PriorityBadge priority={incident.priority} />
            {isAi && (
              <span className="inline-flex items-center gap-1 rounded bg-violet-50 px-2 py-0.5 text-xs font-semibold text-violet-700">
                <Bot className="h-3 w-3" aria-hidden="true" />
                AI
              </span>
            )}
            {incident.source === 'VOICE' && (
              <span className="inline-flex items-center gap-1 rounded bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700">
                <Mic className="h-3 w-3" aria-hidden="true" />
                Voice
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
          aria-label="Close"
        >
          ✕
        </button>
      </header>

      <section className="space-y-4 border-t border-gray-100 pt-4">
        <DetailRow label="Description">
          <p className="whitespace-pre-wrap text-sm text-gray-700">{description}</p>
        </DetailRow>
        <DetailRow label="Property">
          <span className="text-sm text-gray-700">
            {property?.name ?? `Property #${incident.property_id}`}
          </span>
        </DetailRow>
        <DetailRow label="Assignee">
          <span className="text-sm text-gray-700">
            {assigneeUser?.name
              ?? (incident.assignee_user_id ? `User #${incident.assignee_user_id}` : null)
              ?? assigneeTeam?.name
              ?? (incident.assignee_team_id ? `Team #${incident.assignee_team_id}` : null)
              ?? 'Unassigned'}
          </span>
        </DetailRow>
        <DetailRow label="Created by">
          <span className="text-sm text-gray-700">
            {incident.created_by_user_id
              ? (createdByUser?.name ?? `User #${incident.created_by_user_id}`)
              : isAi
                ? 'AI (Gemini extraction)'
                : incident.source === 'VOICE'
                  ? 'AI (Voice flow)'
                  : 'System'}
          </span>
        </DetailRow>

        {/* AI audit trail */}
        {(isAi || incident.source === 'VOICE' || incident.conversation_id != null) && (
          <div className="rounded-lg border border-violet-100 bg-violet-50 p-3 space-y-1.5">
            <p className="text-xs font-semibold uppercase tracking-wide text-violet-600">
              AI extraction
            </p>
            {incident.intent && (
              <AuditField label="Intent" value={incident.intent} />
            )}
            {confidencePct != null && (
              <AuditField label="Confidence" value={`${confidencePct}%`} />
            )}
            
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 rounded-lg bg-gray-50 p-3 text-xs">
          <Timestamp label="Created" value={incident.created_at} />
          <Timestamp label="Due" value={incident.due_at} />
          <Timestamp label="Acknowledged" value={incident.acknowledged_at} />
          <Timestamp label="Started" value={incident.started_at} />
          <Timestamp label="Completed" value={incident.completed_at} />
          <Timestamp label="Updated" value={incident.updated_at} />
        </div>
      </section>

      {!readOnly && !isDraft && transitions.length > 0 && (
        <footer className="mt-5 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-4">
          <span className="mr-1 text-xs font-medium uppercase tracking-wide text-gray-500">
            Move to:
          </span>
          {transitions.map((status) => (
            <button
              key={status}
              type="button"
              disabled={isUpdating}
              onClick={() => onTransition(status)}
              className={`rounded-lg border bg-white px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50 ${STATUS_BUTTON_CLASS[status]}`}
            >
              {STATUS_LABELS[status]}
            </button>
          ))}
        </footer>
      )}
      {readOnly && (
        <p className="mt-5 border-t border-gray-100 pt-4 text-xs italic text-gray-400">
          Status transitions are disabled in preview mode.
        </p>
      )}
    </>
  )
}

function AuditField({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex items-center gap-1.5 text-xs text-violet-700">
      <span className="font-medium text-violet-500">{label}:</span>
      {value}
    </div>
  )
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-gray-400">{label}</p>
      <div className="mt-1">{children}</div>
    </div>
  )
}

function Timestamp({ label, value }: { label: string; value: string | null }): JSX.Element {
  return (
    <div>
      <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400">{label}</p>
      <p className="mt-0.5 text-gray-700">{value ? formatRelative(value) : '—'}</p>
    </div>
  )
}
